import React, { useState } from 'react';
// Make sure to install react-icons: npm install react-icons
import { FaPlus, FaUpload, FaImage, FaArrowLeft } from 'react-icons/fa';

// --- Reusable Components ---

const EventCard = ({ event, isFeatured }) => (
    <div className={`bg-white p-6 rounded-lg shadow-sm border-l-4 ${isFeatured ? 'border-indigo-500' : 'border-transparent'}`}>
        <div className="flex justify-between items-start flex-wrap">
            <div>
                <h3 className="text-xl font-bold text-gray-800">{event.title}</h3>
                <p className="text-sm text-gray-500 mt-1">Organized by: {event.organizer}</p>
            </div>
            <p className="text-sm text-gray-600 whitespace-nowrap mt-2 sm:mt-0">{event.date} • {event.time}</p>
        </div>
        <p className="text-gray-600 my-4">{event.description}</p>
        <div className="flex items-center gap-2 flex-wrap">
            {event.tags.map(tag => (
                <span
                    key={tag}
                    className={`px-3 py-1 text-xs font-semibold rounded-full ${isFeatured ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'
                        }`}
                >
                    {tag}
                </span>
            ))}
        </div>
    </div>
);

// --- Main Events Page Component ---

const EventsPage = () => {
    // State to toggle between 'list' and 'create' views
    const [view, setView] = useState('list');

    // Dummy data for the event list
    const events = [
        {
            title: 'Q4 2023 Startup Pitch Day',
            organizer: 'John Anderson',
            date: 'Dec 20, 2023',
            time: '9:00 AM - 6:00 PM',
            description: 'Join us for our quarterly startup pitch day featuring 10 innovative companies from our incubator program.',
            tags: ['Pitch Day', 'Networking', 'Startups'],
            featured: true,
        },
        {
            title: 'Tech Innovation Summit 2023',
            organizer: 'Sarah Williams',
            date: 'Dec 20, 2023',
            time: '9:00 AM - 6:00 PM',
            description: 'A full-day summit exploring the latest trends in technology and innovation, featuring industry leaders and experts.',
            tags: ['Summit', 'Technology', 'Innovation'],
        },
        {
            title: 'Startup Mentorship Workshop',
            organizer: 'Michael Chen',
            date: 'Dec 22, 2023',
            time: '10:00 AM - 12:00 PM',
            description: 'Interactive workshop focused on building effective mentorship programs for early-stage startups.',
            tags: ['Workshop', 'Mentorship', 'Startups'],
        },
    ];

    // --- View for Listing Events ---
    const EventsListView = () => (
        <div>
            <header className="flex justify-between items-center mb-8">
                <h2 className="text-3xl font-bold text-gray-800">Events</h2>
                <button
                    onClick={() => setView('create')}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg shadow-sm hover:bg-indigo-700"
                >
                    <FaPlus />
                    Create New Event
                </button>
            </header>
            <div className="space-y-6">
                {events.map((event, index) => (
                    <EventCard key={index} event={event} isFeatured={event.featured} />
                ))}
            </div>
        </div>
    );

    // --- View for Creating a New Event ---
    const CreateEventView = () => (
        <div>
            <header className="flex items-center gap-4 mb-8">
                <button
                    onClick={() => setView('list')}
                    className="p-2 rounded-full hover:bg-gray-200"
                    title="Back to list"
                >
                    <FaArrowLeft className="text-gray-600" />
                </button>
                <h2 className="text-2xl font-bold text-gray-800">
                    Create New Event <span className="text-gray-400 font-light">• Apply form</span>
                </h2>
            </header>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 bg-white p-8 rounded-xl shadow-sm">
                    <form className="space-y-6">
                        <div>
                            <label htmlFor="event-title" className="block text-sm font-medium text-gray-700 mb-1">Event Title</label>
                            <input type="text" id="event-title" placeholder="Enter event title" className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                                <input type="date" id="date" className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500" />
                            </div>
                            <div>
                                <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                                <input type="time" id="time" className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500" />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="event-host" className="block text-sm font-medium text-gray-700 mb-1">Event Host</label>
                            <input type="text" id="event-host" placeholder="Enter host name or organization" className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500" />
                        </div>
                        <div>
                            <label htmlFor="event-description" className="block text-sm font-medium text-gray-700 mb-1">Event Description</label>
                            <textarea id="event-description" rows="4" placeholder="Describe your event..." className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"></textarea>
                        </div>
                        <div>
                            <label htmlFor="event-location" className="block text-sm font-medium text-gray-700 mb-1">Event Location</label>
                            <input type="text" id="event-location" placeholder="Enter event location" className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500" />
                        </div>
                        <div>
                            <label htmlFor="event-category" className="block text-sm font-medium text-gray-700 mb-1">Event Category</label>
                            <select id="event-category" className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500">
                                <option>Select a category</option>
                                <option>Technology</option>
                                <option>Business</option>
                                <option>Networking</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="event-apply-link" className="block text-sm font-medium text-gray-700 mb-1">Event apply link</label>
                            <input type="text" id="event-apply-link" placeholder="Enter link" className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500" />
                        </div>
                    </form>
                </div>
                <div className="space-y-8">
                    <div className="bg-white p-8 rounded-xl shadow-sm text-center">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
                            <FaUpload className="mx-auto text-4xl text-gray-400 mb-4" />
                            <p className="text-sm text-gray-600">Drag and drop an image or click to upload</p>
                            <p className="text-xs text-gray-400 mt-1">Supported formats: JPG, PNG, GIF (max 5MB)</p>
                            <button className="mt-4 px-4 py-2 bg-indigo-50 text-indigo-700 font-semibold rounded-lg text-sm">Choose File</button>
                        </div>
                    </div>
                    <div className="bg-white p-8 rounded-xl shadow-sm text-center">
                        <div className="bg-gray-100 rounded-lg h-40 flex flex-col items-center justify-center">
                            <FaImage className="text-4xl text-gray-400 mb-4" />
                            <h4 className="font-semibold text-gray-700">Event Preview</h4>
                            <p className="text-xs text-gray-500 mt-1">Your event details will appear here as you fill the form</p>
                        </div>
                    </div>
                    <div className="flex justify-end gap-4">
                        <button onClick={() => setView('list')} className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg shadow-sm hover:bg-gray-50">Cancel</button>
                        <button onClick={() => setView('list')} className="px-6 py-2 bg-indigo-600 text-white rounded-lg shadow-sm hover:bg-indigo-700">Post Event</button>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="p-8 bg-gray-50 min-h-screen">
            {/* Conditionally render the view based on the state */}
            {view === 'list' ? <EventsListView /> : <CreateEventView />}
        </div>
    );
};

export default EventsPage;
